import React, { useEffect, useRef } from "react";

/**
 * CSS/Canvas star background — replaces Three.js to avoid WebGL context errors.
 * Renders an animated starfield + glowing moon using 2D Canvas only.
 */
export default function ThreeBackground({ type = "stars" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let animId;
    let width, height;
    let stars = [];
    let rings = [];

    const resize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
      if (type === "stars") initStars();
      if (type === "rings") initRings();
    };

    // ── STARS mode ──────────────────────────────────────────────
    function initStars() {
      stars = Array.from({ length: 220 }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 1.4 + 0.2,
        speed: Math.random() * 0.25 + 0.05,
        color:
          Math.random() < 0.5
            ? `rgba(243,112,33,${(Math.random() * 0.6 + 0.4).toFixed(2)})`
            : Math.random() < 0.6
            ? `rgba(212,175,55,${(Math.random() * 0.6 + 0.3).toFixed(2)})`
            : `rgba(255,255,255,${(Math.random() * 0.5 + 0.3).toFixed(2)})`,
        twinkle: Math.random() * Math.PI * 2,
        twinkleSpeed: Math.random() * 0.03 + 0.01,
      }));
    }

    let moonY = 0;
    let tick = 0;

    function drawStars() {
      ctx.clearRect(0, 0, width, height);
      tick += 0.016;

      // Draw stars
      stars.forEach((s) => {
        s.twinkle += s.twinkleSpeed;
        const alpha = 0.4 + Math.sin(s.twinkle) * 0.5;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = s.color.replace(/[\d.]+\)$/, `${alpha.toFixed(2)})`);
        ctx.fill();

        // slowly drift upward + wrap
        s.y -= s.speed;
        if (s.y < -2) {
          s.y = height + 2;
          s.x = Math.random() * width;
        }
      });

      // Moon (top-right area)
      moonY = height * 0.18 + Math.sin(tick * 0.5) * 8;
      const moonX = width * 0.82;
      const moonR = Math.min(width, height) * 0.055;

      // Outer glow
      const grd = ctx.createRadialGradient(moonX, moonY, moonR * 0.5, moonX, moonY, moonR * 2.5);
      grd.addColorStop(0, "rgba(243,112,33,0.18)");
      grd.addColorStop(0.5, "rgba(212,175,55,0.07)");
      grd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(moonX, moonY, moonR * 2.5, 0, Math.PI * 2);
      ctx.fillStyle = grd;
      ctx.fill();

      // Moon body
      const moonGrd = ctx.createRadialGradient(
        moonX - moonR * 0.3, moonY - moonR * 0.3, moonR * 0.1,
        moonX, moonY, moonR
      );
      moonGrd.addColorStop(0, "#F5D87A");
      moonGrd.addColorStop(0.6, "#D4AF37");
      moonGrd.addColorStop(1, "#f37021");
      ctx.beginPath();
      ctx.arc(moonX, moonY, moonR, 0, Math.PI * 2);
      ctx.fillStyle = moonGrd;
      ctx.fill();

      // Shadow bite (crescent)
      ctx.beginPath();
      ctx.arc(moonX + moonR * 0.38, moonY - moonR * 0.28, moonR * 0.72, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(10,10,10,0.92)";
      ctx.fill();

      animId = requestAnimationFrame(drawStars);
    }

    // ── RINGS mode ───────────────────────────────────────────────
    function initRings() {
      rings = [
        { radius: 0, tube: 2, color: "243,112,33", speed: 0.004, angle: 0.4, rot: 0 },
        { radius: 0, tube: 3, color: "212,175,55", speed: -0.003, angle: 0.8, rot: 1 },
        { radius: 0, tube: 4, color: "245,216,122", speed: 0.002, angle: 1.2, rot: 2 },
      ];
    }

    let ringTick = 0;
    function drawRings() {
      ctx.clearRect(0, 0, width, height);
      ringTick += 0.01;

      const cx = width / 2;
      const cy = height / 2;
      const base = Math.min(width, height) * 0.28;

      // Radii
      const radii = [base * 0.55, base * 0.78, base];

      radii.forEach((r, i) => {
        const alpha = 0.45 + Math.sin(ringTick + i) * 0.15;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(ringTick * (i % 2 === 0 ? 1 : -1) * 0.3 + i);

        ctx.beginPath();
        ctx.ellipse(0, 0, r, r * 0.32, 0, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${["243,112,33","212,175,55","245,216,122"][i]},${alpha.toFixed(2)})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.restore();
      });

      // Center glowing sphere
      const pulse = 1 + Math.sin(ringTick * 2) * 0.08;
      const sphereR = base * 0.14 * pulse;
      const sg = ctx.createRadialGradient(cx, cy, 0, cx, cy, sphereR);
      sg.addColorStop(0, "rgba(255,255,255,0.9)");
      sg.addColorStop(0.4, "rgba(243,112,33,0.8)");
      sg.addColorStop(1, "rgba(243,112,33,0)");
      ctx.beginPath();
      ctx.arc(cx, cy, sphereR, 0, Math.PI * 2);
      ctx.fillStyle = sg;
      ctx.fill();

      animId = requestAnimationFrame(drawRings);
    }

    // Init
    resize();
    window.addEventListener("resize", resize);
    if (type === "stars") drawStars();
    else if (type === "rings") drawRings();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, [type]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0 w-full h-full"
      style={{ pointerEvents: "none" }}
    />
  );
}
